import { API_URL } from "env";

import { mixLogOut } from '../services/mixin';
interface OAuthAccountResponse {
    status: boolean;
    msg: string;
    data: any;
}

interface OAuthAccountItem {
    id: number;
    provider: string;
    name?: string;
    /** API không trả token, secret; chỉ báo đã có hay chưa. Sửa tài khoản mà để trống thì giữ giá trị cũ */
    has_access_token?: boolean;
    has_app_secret?: boolean;
    has_page_access_token?: boolean;
    [key: string]: any;
}

interface OAuthAccountListResponse {
    status: boolean;
    msg: string;
    data: {
        total: number;
        current_page: number;
        items: OAuthAccountItem[];
    } | null;
}

async function getOAuthAccounts(params?: {
    search?: string;
    provider?: string;
    page?: number;
}): Promise<OAuthAccountListResponse> {
    try {
        const token = localStorage.getItem('token') || '';
        const query = new URLSearchParams();
        if (params?.search) query.append('search', params.search);
        if (params?.provider) query.append('provider', params.provider);
        if (params?.page) query.append('page', String(params.page));
        const queryString = query.toString();

        const response = await fetch(`${API_URL}/api/v1/be/oauth-accounts${queryString ? `?${queryString}` : ''}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json() as OAuthAccountListResponse;
    } catch (error) {
        console.error('Failed to fetch OAuth accounts:', error);
        return {
            status: false,
            msg: 'Fetch failed',
            data: null,
        };
    }
}

async function connectOAuthAccount(
    provider: string,
    access_token?: string,
    app_secret?: string,
    app_id?: string,
    name?: string
): Promise<OAuthAccountResponse> {
    try {
        const token = localStorage.getItem('token') || '';
        const body: Record<string, string> = { provider };
        if (access_token) body.access_token = access_token;
        if (app_secret) body.app_secret = app_secret;
        if (app_id) body.app_id = app_id;
        if (name) body.name = name;

        const response = await fetch(`${API_URL}/api/v1/be/oauth-account`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json() as OAuthAccountResponse;
    } catch (error) {
        console.error('Failed to connect OAuth account:', error);
        return {
            status: false,
            msg: 'Connection failed',
            data: null,
        };
    }
}

async function updateOAuthAccount(
    id: number,
    provider: string,
    access_token?: string,
    app_secret?: string,
    app_id?: string,
    name?: string
): Promise<OAuthAccountResponse> {
    try {
        const token = localStorage.getItem('token') || '';
        const body: Record<string, string> = { provider };
        if (access_token) body.access_token = access_token;
        if (app_secret) body.app_secret = app_secret;
        if (app_id) body.app_id = app_id;
        if (name) body.name = name;

        const response = await fetch(`${API_URL}/api/v1/be/oauth-account/${id}`, {
            method: 'PUT',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json() as OAuthAccountResponse;
    } catch (error) {
        console.error('Failed to update OAuth account:', error);
        return {
            status: false,
            msg: 'Update failed',
            data: null,
        };
    }
}

async function deleteOAuthAccount(id: number): Promise<OAuthAccountResponse> {
    try {
        const token = localStorage.getItem('token') || '';

        const response = await fetch(`${API_URL}/api/v1/be/oauth-account/${id}`, {
            method: 'DELETE',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json() as OAuthAccountResponse;
    } catch (error) {
        console.error('Failed to delete OAuth account:', error);
        return {
            status: false,
            msg: 'Delete failed',
            data: null,
        };
    }
}

async function syncFanpages(id: number): Promise<OAuthAccountResponse> {
    try {
        const token = localStorage.getItem('token') || '';

        const response = await fetch(`${API_URL}/api/v1/be/oauth-account/${id}/sync-pages`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json() as OAuthAccountResponse;
    } catch (error) {
        console.error('Failed to sync fanpages:', error);
        return {
            status: false,
            msg: 'Sync failed',
            data: null,
        };
    }
}

/** Lấy URL đăng nhập Facebook để xin quyền — GET /oauth-account/{id}/facebook-login-url */
async function getFacebookLoginUrl(
    id: number
): Promise<{ status: boolean; authUrl?: string; msg?: string }> {
    try {
        const token = localStorage.getItem('token') || '';
        const response = await fetch(`${API_URL}/api/v1/be/oauth-account/${id}/facebook-login-url`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (response.status === 401) { mixLogOut(); }
        const json = await response.json().catch(() => ({}));
        if (!json?.status || !json?.data?.auth_url) {
            return { status: false, msg: json?.msg || json?.message };
        }
        return { status: true, authUrl: json.data.auth_url, msg: json.msg };
    } catch (error) {
        console.error('Failed to get Facebook login url:', error);
        return { status: false, msg: 'Fetch failed' };
    }
}

/**
 * Lấy URL cấp quyền TikTok Business — GET /oauth-account/{id}/tiktok-login-url.
 * Backend tự gắn client_key / scope / state; FE chỉ việc điều hướng tới auth_url.
 */
async function getTiktokLoginUrl(
    id: number
): Promise<{ status: boolean; authUrl?: string; msg?: string }> {
    try {
        const token = localStorage.getItem('token') || '';
        const response = await fetch(`${API_URL}/api/v1/be/oauth-account/${id}/tiktok-login-url`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (response.status === 401) { mixLogOut(); }
        const json = await response.json().catch(() => ({}));
        if (!json?.status || !json?.data?.auth_url) {
            return { status: false, msg: json?.msg || json?.message };
        }
        return { status: true, authUrl: json.data.auth_url, msg: json.msg };
    } catch (error) {
        console.error('Failed to get TikTok login url:', error);
        return { status: false, msg: 'Fetch failed' };
    }
}

export { getOAuthAccounts, connectOAuthAccount, updateOAuthAccount, deleteOAuthAccount, syncFanpages, getFacebookLoginUrl, getTiktokLoginUrl };
export type { OAuthAccountItem };
